// @workflow assistant.ui-tools
/**
 * The 3D viewer's UI tools: `ui.viewer.focus_tags`, `ui.viewer.color_tags`, `ui.viewer.set_ghosting`,
 * `ui.viewer.reset`, `ui.viewer.link_360` and
 * the query `ui.viewer.selection`. Pure decisions only; `services/viewerHost.js` wires them to the
 * store, the router, the one Forge viewer (`store.state.viewer`) and the spatial-focus composable.
 *
 *   viewerContract.js   limits, wait budget, skip reasons, progress lines, plain-words details
 *   viewerArgs.js       argument checks (unsupported only for malformed arguments)
 *   viewerColors.js     any hex colour, legend labels
 *   viewerPrepare.js    open the facility and its 3D model before acting (prepare, don't refuse)
 *   viewerOutcome.js    `{ shown, skipped }`: which tags reached the screen, and why not
 *   viewerSelection.js  selected elements → tag ids
 *
 * Host contract (`host.viewer`), beyond the prepare calls listed in viewerPrepare.js:
 *   showTags({ owner, scope, encoding, signal }) → Promise<{ status, subjects, placedKeys }>
 *   zoomToTags({ tagIds, signal }) → Promise<{ status, subjects, placedKeys }>   actions/zoomToTags.js
 *   planTagZoom(tagId)  → Promise<{ status, plan }>   TAGS/NAVIGATE_TAG_SPATIALLY, resolve only
 *   zoomToTag(tagId)    → Promise<{ status }>         TAGS/NAVIGATE_TAG_SPATIALLY (the cube's Zoom to tag in 3D)
 *   reset()             → void
 *   setGhosting({ on, style })  → { on, style }|null   Quick Settings' ghost mode; style null keeps it
 *   readSelection()     → { dbIds, owners: [{ id, dbIds }], parentOf(dbId) }
 *   isLinkedTo360()     → boolean      the 3D view is linked to the open 360 (platform/viewer/linkedViews.js)
 *   canLinkTo360()      → boolean      the Link button's rule: not linked, a 3D view and a 360 open
 *   unlinkFrom360()     → void         the viewer's Unlink button
 *   linkTo360()         → boolean      the viewer's Link button; whether the views are linked now
 *
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable, failed } from './uiToolContract.js';
import { TAG_SPATIAL_RESULT_KINDS, TAG_SPATIAL_VIEWERS } from '../../Tags/utils/tagSpatialNavigation.js';
import { ASSISTANT_FOCUS_OWNER, FOCUS_MODE, VIEWER_DETAIL, VIEWER_FACT, VIEWER_LIMITS } from './viewerContract.js';
import { checkColorArgs, checkFocusArgs, checkGhostingArgs, checkNoArgs } from './viewerArgs.js';
import { colorEncoding } from './viewerColors.js';
import { PREPARE_NEED, prepareViewer } from './viewerPrepare.js';
import { ENTITY_KIND_TAG, focusOutcomeResult } from './viewerOutcome.js';
import { selectionTagIds } from './viewerSelection.js';

/** Spatial-focus entity scope (aiva.api SpatialFocusContracts.cs). */
const SCOPE_KIND_ENTITIES = 'entities';

/** TAGS/NAVIGATE_TAG_SPATIALLY statuses (tagSpatialNavigationExecutor.js). */
const NAVIGATION_STATUS = Object.freeze({ EXECUTED: 'executed', STALE: 'stale' });

const viewerOf = (host) => (host && host.viewer) || null;

/** Every viewer tool needs the app's viewer side; a host without one (a test, another app) is not_available. */
const requireViewerSide = (host) => (viewerOf(host) ? null : notAvailable(VIEWER_DETAIL.NO_VIEWER));

/**
 * focus_tags, color_tags and set_ghosting open what they need first (viewerPrepare.js); focus_tags'
 * `target` says where a missing 3D view goes (color_tags and set_ghosting: always a new one).
 */
const prepareFor = (host, input) => prepareViewer(host, input.facilityId, PREPARE_NEED.MODEL_3D, input.target);

// ---- spatial focus --------------------------------------------------------------------------------

const tagEntity = (id, encodingValue) => (encodingValue === undefined
  ? { kind: ENTITY_KIND_TAG, id }
  : { kind: ENTITY_KIND_TAG, id, encodingValue });

const focusScope = (entities) => ({ kind: SCOPE_KIND_ENTITIES, entities });

async function showTags(host, tagIds, scope, encoding) {
  const outcome = await viewerOf(host).showTags({ owner: ASSISTANT_FOCUS_OWNER, scope, encoding, signal: host.signal || null });
  return focusOutcomeResult(outcome, tagIds);
}

/**
 * AVA-744: while the 3D view is linked to the open 360, the link puts the camera back at the 360
 * and a frame is undone. So before framing several tags (an isolate, or a zoom to several) the
 * views are unlinked, as the viewer's Unlink button does, and the answer says so
 * (VIEWER_FACT.UNLINKED_FROM_360). When nothing was shown the link is put back (the 360 markers the
 * unlink hid stay hidden); not after "replaced", since the newer request owns the view.
 * A throw (the spatial-focus request failed) puts the link back too, unless the request was stopped:
 * an exception carries no "replaced", so a stopped signal is the only sign the view is no longer this request's.
 * @param {() => Promise<object>} frame the work, answering a tool result
 */
async function withViewsUnlinked(host, frame) {
  const viewer = viewerOf(host);
  const wasLinked = viewer.isLinkedTo360();
  if (wasLinked) viewer.unlinkFrom360();
  let result;
  try {
    result = await frame();
  } catch (err) {
    if (wasLinked && !(host.signal && host.signal.aborted)) viewer.linkTo360();
    throw err;
  }
  if (!wasLinked) return result;
  if (result.ok) return ok({ ...result.data, [VIEWER_FACT.UNLINKED_FROM_360]: true });
  if (result.detail !== VIEWER_DETAIL.REPLACED) viewer.linkTo360();
  return result;
}

/**
 * Isolate and frame tags through the spatial-focus scene, answered `{ shown, skipped }` per tag:
 * focus_tags' isolate, also used by `ui.tasks.show_in_3d` away from the task board (taskTools.js).
 */
export function isolateTags(host, tagIds) {
  return withViewsUnlinked(host, () => showTags(host, tagIds, focusScope(tagIds.map((id) => tagEntity(id))), null));
}

/**
 * Zoom to several tags without isolating them (actions/zoomToTags.js through the host): framed,
 * shown and marked, answered `{ shown, skipped }` per tag like the isolate.
 */
function zoomToTags(host, tagIds) {
  return withViewsUnlinked(host, async () => {
    const outcome = await viewerOf(host).zoomToTags({ tagIds, signal: host.signal || null });
    return focusOutcomeResult(outcome, tagIds);
  });
}

// ---- zoom to one tag ------------------------------------------------------------------------------

/** Only a 3D plan in this facility may move the camera; anything else is answered first. */
function zoomPlanVerdict(resolved) {
  if (resolved && resolved.status === NAVIGATION_STATUS.STALE) return failed(VIEWER_DETAIL.REPLACED);
  const plan = resolved && resolved.plan;
  if (!plan) return failed(VIEWER_DETAIL.TAG_NOT_FOUND);
  if (plan.kind === TAG_SPATIAL_RESULT_KINDS.NAVIGATE_FACILITY || plan.kind === TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY) {
    return notAvailable(VIEWER_DETAIL.OTHER_FACILITY);
  }
  if (plan.kind !== TAG_SPATIAL_RESULT_KINDS.VIEWER || plan.viewer !== TAG_SPATIAL_VIEWERS.MODEL_3D) return failed(VIEWER_DETAIL.TAG_NO_PLACE);
  return null;
}

function zoomResult(execution, tagId) {
  const status = execution && execution.status;
  if (status === NAVIGATION_STATUS.EXECUTED) return ok({ shown: [tagId], skipped: [] });
  return failed(status === NAVIGATION_STATUS.STALE ? VIEWER_DETAIL.REPLACED : VIEWER_DETAIL.ZOOM_FAILED);
}

async function zoomToOneTag(host, tagId) {
  const viewer = viewerOf(host);
  const verdict = zoomPlanVerdict(await viewer.planTagZoom(tagId));
  return verdict || zoomResult(await viewer.zoomToTag(tagId), tagId);
}

/**
 * Zoom (the default) never isolates: one tag zooms exactly as the tag cube's "Zoom to tag in 3D"
 * (the same store action); several are framed, shown and marked together. Isolate shows only the tags.
 */
function runFocus({ tagIds, mode }, host) {
  if (mode === FOCUS_MODE.ISOLATE) return isolateTags(host, tagIds);
  return tagIds.length === 1 ? zoomToOneTag(host, tagIds[0]) : zoomToTags(host, tagIds);
}

function runColor({ groups }, host) {
  const entities = groups.flatMap((group) => group.tagIds.map((id) => tagEntity(id, group.value)));
  const tagIds = groups.flatMap((group) => group.tagIds);
  return showTags(host, tagIds, focusScope(entities), colorEncoding(groups.map((g) => ({ value: g.value, color: g.color }))));
}

/** Ghost mode changes only the display (no data): `{ on, style }` as the viewer now shows it. */
async function runGhosting({ on, style }, host) {
  const shown = await viewerOf(host).setGhosting({ on, style });
  return shown ? ok({ on: shown.on, style: shown.style }) : failed(VIEWER_DETAIL.NO_VIEWER);
}

// ---- reset and selection: no viewer to open for these --------------------------------------------

const isModelLoaded = (state) => !!(state.hasModel && state.hasInstanceTree && state.hasMainExtension);

/**
 * Reset and selection never open a viewer: with no 3D viewer there is nothing to clear and nothing
 * selected, so they answer at once (`idle`). While a model loads they answer not_available.
 * @returns {{ result: object }|null} the answer to give now, or null to go on
 */
function idleAnswer(host, idle) {
  const state = viewerOf(host).readState(null);
  if (!state || !state.hasViewer) return { result: idle };
  return isModelLoaded(state) ? null : { result: notAvailable(VIEWER_DETAIL.LOADING) };
}

async function runReset(_, host) {
  const early = idleAnswer(host, ok());
  if (early) return early.result;
  await viewerOf(host).reset();
  return ok();
}

/**
 * Link the 3D view to the open 360 again, as the viewer's Link button does (the next step after
 * an isolate unlinked them). Already linked is done; nothing to link to is not_available, and a
 * link that did not take is failed, never "linked". Never opens a 360 or a 3D view.
 */
function runLink360(_, host) {
  const viewer = viewerOf(host);
  if (viewer.isLinkedTo360()) return ok({ linked: true });
  if (!viewer.canLinkTo360()) return notAvailable(VIEWER_DETAIL.NO_360);
  return viewer.linkTo360() ? ok({ linked: true }) : failed(VIEWER_DETAIL.LINK_FAILED);
}

/** The ids come back capped at the id limit; `count` is always the full number. */
function runSelection(_, host) {
  const early = idleAnswer(host, ok({ tagIds: [], count: 0 }));
  if (early) return early.result;
  const tagIds = selectionTagIds(viewerOf(host).readSelection());
  return ok({ tagIds: tagIds.slice(0, VIEWER_LIMITS.MAX_TAG_IDS), count: tagIds.length });
}

// ---- the tools ---------------------------------------------------------------------------------------

/**
 * Registry entries: `check` validates arguments, `ready` gets the viewer ready (or answers why it
 * can't), `run` works. focus/colour/ghosting prepare the viewer; reset/selection only need the viewer side.
 */
export const VIEWER_ACTIONS = Object.freeze({
  [UI_TOOL.VIEWER_FOCUS_TAGS]: { check: checkFocusArgs, ready: prepareFor, run: runFocus },
  [UI_TOOL.VIEWER_COLOR_TAGS]: { check: checkColorArgs, ready: prepareFor, run: runColor },
  [UI_TOOL.VIEWER_SET_GHOSTING]: { check: checkGhostingArgs, ready: prepareFor, run: runGhosting },
  [UI_TOOL.VIEWER_RESET]: { check: checkNoArgs, ready: requireViewerSide, run: runReset },
  [UI_TOOL.VIEWER_LINK_360]: { check: checkNoArgs, ready: requireViewerSide, run: runLink360 },
});

export const VIEWER_QUERIES = Object.freeze({
  [UI_TOOL.VIEWER_SELECTION]: { check: checkNoArgs, ready: requireViewerSide, run: runSelection },
});
